/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  conditions: [],
  selectedCondition: null,

  isLoading: false,
  isError: false,

  searchResults: [],
  isSearching: false,
  searchError: false,

  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    next: null,
  },

  filters: {
    q: '',
    active: null,
  },
};

const getters = {};

const mutations = {
  SET_CONDITIONS(state, list) { state.conditions = list; },
  SET_SELECTED_CONDITION(state, condition) { state.selectedCondition = condition; },
  SET_IS_LOADING(state, value) { state.isLoading = value; },
  SET_IS_ERROR(state, value) { state.isError = value; },
  SET_SEARCH_RESULTS(state, list) { state.searchResults = list; },
  SET_IS_SEARCHING(state, value) { state.isSearching = value; },
  SET_SEARCH_ERROR(state, value) { state.searchError = value; },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination };
  },
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },
  RESET_FILTERS(state) {
    state.filters = { q: '', active: null };
    state.pagination.page = 1;
  },
};

const actions = {
  async fetchConditions({ commit, state, rootGetters }, filters = null) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const params = {
        page: state.pagination.page,
        page_size: state.pagination.pageSize,
      };

      if (filters) {
        if (filters.q) params.q = filters.q;
        if (filters.active !== null && filters.active !== undefined) {
          params.active = filters.active;
        }
      } else {
        if (state.filters.q) params.q = state.filters.q;
        if (state.filters.active !== null && state.filters.active !== undefined) {
          params.active = state.filters.active;
        }
      }

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions`),
        { params }
      );

      const conditions = res.data.results?.map(item => item.condition) || [];
      commit('SET_CONDITIONS', conditions);
      commit('SET_PAGINATION', {
        page: res.data.page,
        pageSize: res.data.page_size || state.pagination.pageSize,
        total: res.data.total,
        next: res.data.next,
      });

      return res.data;
    } catch (error) {
      console.error('Fetch conditions failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async getCondition({ commit, rootGetters }, conditionId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions/${conditionId}`)
      );
      commit('SET_SELECTED_CONDITION', res.data);
      return res.data;
    } catch (error) {
      console.error('Get condition failed:', error);
      throw error.response?.data || error;
    }
  },

  async createCondition({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions`),
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (error) {
      console.error('Create condition failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateCondition({ rootGetters }, { conditionId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions/${conditionId}`),
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (error) {
      console.error('Update condition failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteCondition({ rootGetters }, conditionId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions/${conditionId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete condition failed:', error);
      throw error.response?.data || error;
    }
  },

  async setPage({ commit, dispatch }, page) {
    commit('SET_PAGINATION', { page });
    return dispatch('fetchConditions');
  },

  async setPageSize({ commit, dispatch }, pageSize) {
    commit('SET_PAGINATION', { pageSize, page: 1 });
    return dispatch('fetchConditions');
  },

  async setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters);
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchConditions');
  },

  async resetFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    return dispatch('fetchConditions');
  },

  async searchConditions({ commit, rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_SEARCHING', true);
      commit('SET_SEARCH_ERROR', false);

      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/conditions/search`),
        payload
      );

      const results = res.data.results?.map(item => item.condition) || [];
      commit('SET_SEARCH_RESULTS', results);
      return res.data;
    } catch (error) {
      console.error('Search conditions failed:', error);
      commit('SET_SEARCH_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_SEARCHING', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
